import {
  CapabilityCoordinatorBase,
  CapabilityRegistry,
  CapabilityRegistryContainer,
  CapabilitiesCollection,
} from '@trimble/web3d';
import {
  PathNavigationCapability,
  CrossPaneFrustumsCapability,
  ImageCapabilityBase,
  PanoramaCapability,
  PlanarCapability,
  StationsNavigationCapability,
} from '@trimble/web3d-capabilities';
import {
  arePointsEqual,
  areQuaternionsEqual,
  Point2D,
  Point3D,
} from '@trimble/utilities';
import { QuaternionTuple } from '@technology/web3d-plugin-threejs';
import { getLogger } from '@trimble/logging';
import { createEffect, onCleanup } from 'solid-js';
import { ViewModeCapabilityCoordinator } from './view-mode-capability-coordinator';

type FrustumSourceCapability =
  | StationsNavigationCapability
  | PlanarCapability
  | PanoramaCapability;

type FrustumPosition = Point3D | Point2D | null;

interface CachedFrustumSource {
  readonly id: string;
  readonly targetPosition: FrustumPosition;
  readonly quaternion: QuaternionTuple | null;
  readonly fov: number | null;
}

interface PaneState {
  cachedSources: CachedFrustumSource[] | null;
  crossSectionCleared: boolean;
  isUpdating: boolean;
}

/**
 * Global coordinator that distributes cross-pane frustum snapshots.
 *
 * A single `requestAnimationFrame` loop iterates all pane containers, builds
 * frustum snapshots using the **receiving pane's mode** (matching the old
 * per-pane behaviour), and feeds each pane all global snapshots. This replaces
 * the previous per-pane coordinator that ran N separate rAF loops each reading
 * the same global capability lists (O(N^2) reads per frame).
 */
export class CrossPaneFrustumsCoordinator extends CapabilityCoordinatorBase<CapabilityRegistry> {
  private animationFrame: number | null = null;
  private readonly paneStates = new Map<
    CapabilityRegistryContainer,
    PaneState
  >();

  constructor(private readonly capabilityRegistry: CapabilityRegistry) {
    super(capabilityRegistry);

    createEffect(() => {
      const containers = this.capabilityRegistry.paneContainers();
      if (containers.length > 0) {
        this.start();
      } else {
        this.stop();
      }

      onCleanup(() => {
        this.stop();
      });
    });
  }

  public async cleanup(): Promise<void> {
    this.stop();
    const containers = this.capabilityRegistry.paneContainers();
    const clearPromises = containers
      .map((c) => c.get(CrossPaneFrustumsCapability)()?.clearFrustums())
      .filter((p): p is Promise<void> => p != null);
    await Promise.all(clearPromises);
    this.paneStates.clear();
  }

  private start(): void {
    if (this.animationFrame != null) {
      return;
    }
    this.animationFrame = requestAnimationFrame(this.tick);
  }

  private stop(): void {
    if (this.animationFrame == null) {
      return;
    }
    cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
    this.paneStates.clear();
  }

  private getOrCreatePaneState(
    container: CapabilityRegistryContainer
  ): PaneState {
    let state = this.paneStates.get(container);
    if (!state) {
      state = {
        cachedSources: null,
        crossSectionCleared: false,
        isUpdating: false,
      };
      this.paneStates.set(container, state);
    }
    return state;
  }

  private readonly tick = (): void => {
    const containers = this.capabilityRegistry.paneContainers();
    if (containers.length === 0) {
      this.stop();
      return;
    }

    for (const key of this.paneStates.keys()) {
      if (!containers.includes(key)) {
        this.paneStates.delete(key);
      }
    }

    for (const container of containers) {
      this.updatePane(container);
    }

    this.animationFrame = requestAnimationFrame(this.tick);
  };

  private updatePane(container: CapabilityRegistryContainer): void {
    const mode = container
      .get(ViewModeCapabilityCoordinator)()
      ?.viewMode().type;
    const capability = container.get(CrossPaneFrustumsCapability)();
    if (!capability) {
      return;
    }

    if (mode !== '2D' && mode !== '3D') {
      const state = this.getOrCreatePaneState(container);
      if (state.cachedSources !== null) {
        void capability.clearFrustums();
        state.cachedSources = null;
        state.crossSectionCleared = false;
      }
      return;
    }

    if (mode === '3D') {
      const alignNav = container.get(PathNavigationCapability)();
      if (alignNav?.crossSectionEnabled()) {
        const state = this.getOrCreatePaneState(container);
        if (!state.crossSectionCleared) {
          void capability.clearFrustums();
          state.crossSectionCleared = true;
        }
        return;
      }
    }

    const state = this.getOrCreatePaneState(container);
    state.crossSectionCleared = false;

    if (!capability.isActive()) {
      state.cachedSources = null;
      return;
    }

    if (state.isUpdating) {
      return;
    }

    const entries = this.collectCapabilities();
    const currentSources =
      CrossPaneFrustumsCoordinator.readAllPositions(entries);
    if (!hasSourcesChanged(currentSources, state.cachedSources)) {
      return;
    }

    const snapshots = currentSources.map(
      ({ id, targetPosition, quaternion, fov }) => ({
        id,
        data: { targetPosition, quaternion, fov },
      })
    );
    state.isUpdating = true;
    void capability
      .updateFrustums(snapshots)
      .then(() => {
        if (capability.isActive()) {
          state.cachedSources = currentSources;
        }
      })
      .catch((error: unknown) => {
        getLogger().error('Failed to update cross-pane frustums', error);
      })
      .finally(() => {
        state.isUpdating = false;
      });
  }

  private static readAllPositions(
    entries: Array<{ id: string; capability: FrustumSourceCapability }>
  ): CachedFrustumSource[] {
    return entries.map(({ id, capability }) => {
      const pos = capability.getFrustumPosition();
      return {
        id,
        targetPosition: pos.targetPosition ?? null,
        quaternion: pos.quaternion ?? null,
        fov: pos.fov ?? null,
      };
    });
  }

  private collectCapabilities(): Array<{
    id: string;
    capability: FrustumSourceCapability;
  }> {
    const { panoramaCaps, planarCaps } = this.collectImageCaps();
    const imageEntries = [
      ...panoramaCaps.map((capability, index) => ({
        id: `panorama-${index.toString()}`,
        capability,
      })),
      ...planarCaps.map((capability, index) => ({
        id: `planar-${index.toString()}`,
        capability,
      })),
    ];

    const stationNavigationCaps = this.capabilityRegistry
      .getPaneCapabilities(StationsNavigationCapability)
      .filter((cap) => cap.isActive());

    return [
      ...stationNavigationCaps.map((capability, index) => ({
        id: `station-${index.toString()}`,
        capability,
      })),
      ...imageEntries,
    ];
  }

  private collectImageCaps(): {
    panoramaCaps: PanoramaCapability[];
    planarCaps: PlanarCapability[];
  } {
    const imageCaps = this.capabilityRegistry
      .getPaneCapabilities(CapabilitiesCollection<ImageCapabilityBase>)
      .flatMap((collection) => collection.capabilities);
    const panoramaCaps = imageCaps
      .filter(
        (capability): capability is PanoramaCapability =>
          capability instanceof PanoramaCapability
      )
      .filter((capability) => capability.hasLoadedImage());
    const planarCaps = imageCaps
      .filter(
        (capability): capability is PlanarCapability =>
          capability instanceof PlanarCapability
      )
      .filter((capability) => capability.hasLoadedImage());
    return { panoramaCaps, planarCaps };
  }
}

function hasSourcesChanged(
  current: CachedFrustumSource[],
  cached: CachedFrustumSource[] | null
): boolean {
  if (!cached || current.length !== cached.length) {
    return true;
  }
  for (let i = 0; i < current.length; i++) {
    const cur = current[i];
    const prev = cached[i];
    if (
      cur.id !== prev.id ||
      !positionsEqual(cur.targetPosition, prev.targetPosition) ||
      !areQuaternionsEqual(cur.quaternion, prev.quaternion) ||
      cur.fov !== prev.fov
    ) {
      return true;
    }
  }
  return false;
}

function positionsEqual(a: FrustumPosition, b: FrustumPosition): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return arePointsEqual(a, b);
}
